import React, { useState, useRef, useEffect } from "react";
import "../styles/PillarsSection.css";

const pillarsData = [
  {
    title: "Business",
    description:
      "This is the foundation of sustainable growth. It ensures that your business operates within a clear and compliant framework, setting the stage for scalability.",
    rectangles: [
      "Legal Structuring",
      "Quality Lead Generation",
      "Compliance & Regulations",
    ],
  },
  {
    title: "Marketing",
    description:
      "Once the foundation is solid, marketing becomes the next step. This is about telling your story, creating visibility, attracting the right audience, and building relationships.",
    rectangles: [
      "Golden Circle & Content Strategy",
      "Brand Exposure & Strategic Partnerships",
      "Community Building & Engagement",
      "Product-Market Fit",
    ],
  },
  {
    title: "Financial",
    description:
      "With a solid foundation and a growing audience, financial growth focuses on optimizing resources, increasing revenue, and scaling sustainably.",
    rectangles: [
      "Token Sales",
      "Preparing for Investors",
      "Over-The-Counter (OTC) Desks",
    ],
  },
];

/**
 * Return a line height suitable for each pillar:
 * - Marketing (index 1) has more rectangles => bigger line
 */
function getLineHeight(index) {
  // Example: 220 for Marketing, 180 for others
  return index === 1 ? 220 : 180;
}

function getActiveColor(index) {
  switch (index) {
    case 0: // Business
      return "#19B4C1";
    case 1: // Marketing
      return "#1292E0";
    case 2: // Financial
      return "#0E436C";
    default:
      return "white";
  }
}

export default function PillarsSection() {
  // Start with Marketing (index 1) as active
  const [activeIndex, setActiveIndex] = useState(1);

  // We'll track the drag start to measure swipes
  const dragStartX = useRef(null);

  // Track whether we’re fading out the rectangles
  const [isFadingOut, setIsFadingOut] = useState(false);

  // Track whether the line is currently visible or hidden
  const [lineIsVisible, setLineIsVisible] = useState(true);

  // Detect mobile viewport (here, width less than 768px)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Function to change active pillar when an inactive circle is clicked
  const handleCircleClick = (index) => {
    if (!isFadingOut && index !== activeIndex) {
      setIsFadingOut(true);
      setLineIsVisible(false);

      setTimeout(() => {
        setActiveIndex(index);
        setIsFadingOut(false);

        setTimeout(() => {
          setLineIsVisible(true);
        }, 1000);
      }, 300);
    }
  };

  // Prevent multiple quick swipes while fading
  const handleNext = () => {
    if (isFadingOut) return;
    fadeOutThenChangeIndex("next");
  };

  const handlePrev = () => {
    if (isFadingOut) return;
    fadeOutThenChangeIndex("prev");
  };

  /**
   * Fade out for 300ms, then switch index, then fade back in (rectangles).
   * Meanwhile, the line also fades out immediately, and we fade it back in
   * 1 second after the rectangles are re‐rendered.
   */
  const fadeOutThenChangeIndex = (direction) => {
    setIsFadingOut(true);
    setLineIsVisible(false);

    setTimeout(() => {
      setActiveIndex((prev) => {
        if (direction === "next") {
          return prev === pillarsData.length - 1 ? 0 : prev + 1;
        } else {
          return prev === 0 ? pillarsData.length - 1 : prev - 1;
        }
      });

      setIsFadingOut(false);

      setTimeout(() => {
        setLineIsVisible(true);
      }, 500);
    }, 300);
  };

  // Called on mouse/touch down
  const handleDragStart = (e) => {
    const startX = e.type === "touchstart" ? e.touches[0].clientX : e.clientX;
    dragStartX.current = startX;
  };

  // Called on mouse/touch up
  const handleDragEnd = (e) => {
    if (dragStartX.current === null) return;
    const endX =
      e.type === "touchend" ? e.changedTouches[0].clientX : e.clientX;
    const diff = endX - dragStartX.current;

    if (diff > 50) handlePrev();
    else if (diff < -50) handleNext();

    dragStartX.current = null;
  };

  return (
    <section className="pillars-section">
      <h2>Our Pillars</h2>

      <div className="pillars-wrapper">
        {/* The vertical line behind circles & rectangles */}
        <div
          className="connecting-line"
          style={{
            height: getLineHeight(activeIndex) + "px",
            backgroundColor: getActiveColor(activeIndex),
          }}
          data-animation={lineIsVisible ? "fade-in-2" : "fade-out-2"}
        />

        {/* Carousel drag area */}
        {isMobile ? (
          // MOBILE VERSION: circle-container displayed as a column with the active circle at the bottom.
          <div
            className="pillars-carousel"
            onMouseDown={handleDragStart}
            onMouseUp={handleDragEnd}
            onTouchStart={handleDragStart}
            onTouchEnd={handleDragEnd}
          >
            <div className="circle-container-mobile">
              {pillarsData.map((pillar, index) => {
                let circleClass = "pillar-circle mobile";
                if (index === activeIndex) {
                  circleClass += " mobile-active";
                } else if (
                  index ===
                  (activeIndex === 0 ? pillarsData.length - 1 : activeIndex - 1)
                ) {
                  circleClass += " mobile-top";
                } else if (
                  index ===
                  (activeIndex === pillarsData.length - 1 ? 0 : activeIndex + 1)
                ) {
                  circleClass += " mobile-middle";
                } else {
                  circleClass += " hidden";
                }

                const isActive = index === activeIndex;
                const circleBg = isActive ? getActiveColor(index) : "#fff";
                const circleText = isActive ? "#fff" : getActiveColor(index);
                const circleBorder = isActive
                  ? "none"
                  : `3px solid ${getActiveColor(index)}`;

                return (
                  <div
                    key={pillar.title}
                    className={circleClass}
                    onClick={
                      !isActive ? () => handleCircleClick(index) : undefined
                    }
                    style={{
                      backgroundColor: circleBg,
                      border: circleBorder,
                      color: circleText,
                      cursor: !isActive ? "pointer" : "default",
                    }}
                  >
                    <span className="circle-title">{pillar.title}</span>
                    {isActive && (
                      <div className="circle-description">
                        {pillar.description}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          // DESKTOP VERSION: circle-container arranged in the original horizontal style.
          <div
            className="pillars-carousel"
            onMouseDown={handleDragStart}
            onMouseUp={handleDragEnd}
            onTouchStart={handleDragStart}
            onTouchEnd={handleDragEnd}
          >
            <div className="circle-container">
              {pillarsData.map((pillar, index) => {
                let circleClass = "pillar-circle";
                if (index === activeIndex) {
                  circleClass += " active";
                } else if (
                  index === activeIndex - 1 ||
                  (activeIndex === 0 && index === pillarsData.length - 1)
                ) {
                  circleClass += " left";
                } else if (
                  index === activeIndex + 1 ||
                  (activeIndex === pillarsData.length - 1 && index === 0)
                ) {
                  circleClass += " right";
                } else {
                  circleClass += " hidden";
                }

                const isActive = index === activeIndex;
                const circleBg = isActive ? getActiveColor(index) : "#fff";
                const circleText = isActive ? "#fff" : getActiveColor(index);
                const circleBorder = isActive
                  ? "none"
                  : `3px solid ${getActiveColor(index)}`;

                return (
                  <div
                    key={pillar.title}
                    className={circleClass}
                    onClick={
                      !isActive ? () => handleCircleClick(index) : undefined
                    }
                    style={{
                      backgroundColor: circleBg,
                      border: circleBorder,
                      color: circleText,
                      cursor: !isActive ? "pointer" : "default",
                    }}
                  >
                    <span className="circle-title">{pillar.title}</span>
                    {isActive && (
                      <div className="circle-description">
                        {pillar.description}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Pillar details (rectangles), fade out/in with .fade-out */}
        <div className={`pillar-details ${isFadingOut ? "fade-out" : ""}`}>
          <div className="rectangle-list">
            {pillarsData[activeIndex].rectangles.map((text, i) => {
              // We only show rectangles for the *active* pillar, so by definition
              // isActive = true here. Give it the same color as its circle:
              const rectangleBorder = `2px solid ${getActiveColor(
                activeIndex
              )}`;
              return (
                <div
                  key={i}
                  className="rectangle"
                  style={{
                    border: rectangleBorder,
                  }}
                >
                  {text}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Dot indicators at the bottom */}
      <div className="dots-indicator">
        {pillarsData.map((_, dotIndex) => (
          <span
            key={dotIndex}
            className={`dot ${activeIndex === dotIndex ? "active" : ""}`}
            onClick={() => {
              if (!isFadingOut && dotIndex !== activeIndex) {
                setIsFadingOut(true);
                setLineIsVisible(false);

                setTimeout(() => {
                  setActiveIndex(dotIndex);
                  setIsFadingOut(false);

                  setTimeout(() => {
                    setLineIsVisible(true);
                  }, 1000);
                }, 300);
              }
            }}
          />
        ))}
      </div>
    </section>
  );
}
